/**
 * cdsAudit.ts
 *
 * Thin, schema-stable wrapper around the audit logger for CDS. Every event:
 *  - carries a machine-queryable `actionType` plus a human phrase,
 *  - carries the order/payment identifiers so a single transaction can be
 *    followed across CDS and POS (both upload under the same device id),
 *  - is fire-and-forget, so logging can never delay or break a payment.
 *
 * `logCdsEventOnce` exists because most of CDS is driven by re-emitted order
 * event batches and a 1s global interval — the same state is observed over and
 * over. Anything on those paths must be keyed so it records once per order or
 * per payment request instead of once per observation.
 */
import { App } from '@oolio-group/domain';
import { AuditLogTags, logger } from '@oolio-group/audit-sdk';

export type CdsAuditAction =
  // Tap2Pay embedded
  | 'T2P_LAUNCH_RECEIVED'
  | 'T2P_LAUNCH_SKIPPED'
  | 'T2P_TIP_SHOWN'
  | 'T2P_TIP_SELECTED'
  | 'T2P_TIP_SKIPPED'
  | 'T2P_TIP_DISMISSED'
  | 'T2P_CHARGE_STARTED'
  | 'T2P_ADYEN_PAY_COMPLETED'
  | 'T2P_COMPLETED'
  | 'T2P_DECLINED'
  | 'T2P_OUTCOME_UNKNOWN'
  | 'T2P_CANCELLED'
  | 'T2P_ERROR'
  | 'T2P_ABORT_REQUESTED'
  | 'T2P_CANCEL_SYNCED'
  | 'T2P_CANCEL_SYNC_FAILED'
  | 'T2P_RESULT_PUSHED'
  | 'T2P_RESULT_PUSH_FAILED'
  | 'T2P_DEEPLINK_LAUNCHED'
  | 'T2P_DEEPLINK_FAILED'
  | 'T2P_SDK_INIT'
  | 'T2P_SDK_INIT_FAILED'
  | 'T2P_WARMUP_FAILED'
  | 'T2P_DISPLAY_SETTINGS_FAILED'
  | 'T2P_VIRTUAL_TERMINAL_FAILED'
  // Receipt + customer
  | 'RECEIPT_SEND_REQUESTED'
  | 'RECEIPT_SEND_SUCCEEDED'
  | 'RECEIPT_SEND_FAILED'
  | 'RECEIPT_SCREEN_ABANDONED'
  | 'CUSTOMER_CREATED'
  | 'CUSTOMER_UPDATED'
  // Loyalty
  | 'LOYALTY_CUSTOMER_ASSIGNED'
  | 'LOYALTY_REWARD_APPLIED'
  | 'LOYALTY_REWARD_REMOVED'
  // Device + session
  | 'APP_STARTED'
  | 'DEVICE_PAIRED'
  | 'DEVICE_UNPAIRED'
  | 'DEVICE_LOGGED_OUT'
  | 'NETWORK_ONLINE'
  | 'NETWORK_OFFLINE'
  | 'LOCAL_COMMS_SYNC_FAILED'
  | 'ORDER_SUBSCRIPTION_ERROR'
  | 'ORDER_SUBSCRIPTION_CLOSED'
  | 'AUDIT_UPLOAD_SKIPPED'
  // Order stream
  | 'ORDER_STARTED'
  | 'ORDER_CLOSED'
  | 'ORDER_EVENTS_DISCARDED'
  | 'SCREEN_CHANGED';

export interface CdsAuditEvent {
  /** Stable machine-queryable action name. */
  actionType: CdsAuditAction;
  tag: AuditLogTags;
  /** Human phrase; becomes the message, suffixed with the order number. */
  phrase: string;
  orderId?: string;
  orderNumber?: string;
  /** Payment request id — the join key between CDS and POS. */
  requestId?: string;
  /** Elapsed time in ms for spans that wrap an async boundary. */
  durationMs?: number;
  level?: 'debug' | 'info' | 'warn' | 'error';
  /** Additional vital info (amounts, statuses, reasons, etc.). */
  context?: Record<string, unknown>;
}

export const logCdsEvent = ({
  actionType,
  tag,
  phrase,
  orderId,
  orderNumber,
  requestId,
  durationMs,
  level = 'info',
  context,
}: CdsAuditEvent): void => {
  try {
    logger
      .logAuditEvent({
        tag,
        level,
        message: orderNumber ? `${phrase} (order #${orderNumber})` : phrase,
        context: {
          ...(context ?? {}),
          actionType,
          source: App.CUSTOMER_DISPLAY_APP,
          ...(orderId ? { orderId } : {}),
          ...(orderNumber ? { orderNumber } : {}),
          ...(requestId ? { paymentRequestId: requestId } : {}),
          ...(durationMs !== undefined ? { durationMs } : {}),
        },
      })
      .catch(() => undefined);
  } catch {
    // Audit logging must never break the flow it is observing.
  }
};

const HANDLED_KEY_LIMIT = 200;
const handledKeys = new Set<string>();

/**
 * Logs `event` only the first time `key` is seen. Use on anything driven by the
 * order event stream or the global interval, where the same state is re-observed
 * continuously.
 */
export const logCdsEventOnce = (key: string, event: CdsAuditEvent): void => {
  if (handledKeys.has(key)) return;

  handledKeys.add(key);
  if (handledKeys.size > HANDLED_KEY_LIMIT) {
    const oldest = handledKeys.values().next().value;
    if (oldest) handledKeys.delete(oldest);
  }

  logCdsEvent(event);
};

/** Test seam — the key guard is module-level state. */
export const resetCdsAuditKeys = (): void => {
  handledKeys.clear();
};
